import L from "leaflet";
import { useEffect, useRef } from "react";
import {
  addPathologyImageOverlay,
  isPathologyOverlayRenderable,
  normalizePathologyOverlay,
} from "./pathologyOverlayUtils";

function escapeHtml(text) {
  if (text == null || typeof text !== "string") return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Renders pathology overlays + educational pins on the Leaflet map.
 * Completely separate from slide pins — never touches drawnItems.
 */
export default function PathologyMapLayers({
  mapRef,
  mapReadyVersion,
  overlays = [],
  previewOverlay = null,
  previewOpacity = 0.85,
  showPins = true,
}) {
  const overlayLayersRef = useRef([]);
  const pinMarkersRef = useRef([]);
  const previewLayerRef = useRef(null);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    overlayLayersRef.current.forEach((layer) => {
      try {
        map.removeLayer(layer);
      } catch (_) {
        /* ignore */
      }
    });
    overlayLayersRef.current = [];

    pinMarkersRef.current.forEach((m) => {
      try {
        map.removeLayer(m);
      } catch (_) {
        /* ignore */
      }
    });
    pinMarkersRef.current = [];

    const accepted = (overlays || [])
      .map(normalizePathologyOverlay)
      .filter(
        (ov) => ov && ov.status === "accepted" && isPathologyOverlayRenderable(ov)
      );

    const previewId = previewOverlay?.id;

    accepted.forEach((ov) => {
      if (previewId && ov.id === previewId) return;
      const layer = addPathologyImageOverlay(map, ov, {
        opacity: 1,
        className: "pathology-ai-overlay",
        zIndex: 650,
      });
      if (layer) overlayLayersRef.current.push(layer);

      if (showPins && Array.isArray(ov.pins)) {
        const bounds = L.latLngBounds(
          [ov.bounds_json.southWest.lat, ov.bounds_json.southWest.lng],
          [ov.bounds_json.northEast.lat, ov.bounds_json.northEast.lng]
        );
        ov.pins.forEach((pin) => {
          let latlng = null;
          if (pin.locations?.data?.geometry?.coordinates) {
            const [lng, lat] = pin.locations.data.geometry.coordinates;
            latlng = [lat, lng];
          } else if (pin.pin_x != null && pin.pin_y != null && bounds) {
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();
            const lat =
              sw.lat + (ne.lat - sw.lat) * (Number(pin.pin_y) / 100);
            const lng =
              sw.lng + (ne.lng - sw.lng) * (Number(pin.pin_x) / 100);
            latlng = [lat, lng];
          }
          if (!latlng) return;
          const marker = L.circleMarker(latlng, {
            radius: 7,
            color: "#c62828",
            fillColor: "#ef5350",
            fillOpacity: 0.95,
            weight: 2,
          }).addTo(map);
          const title = pin.name || pin.disease_label || "Disease";
          const description = pin.description || "";
          const severityLine =
            pin.severity && pin.severity !== title
              ? `<div style="font-size:12px;color:#666;margin-top:2px;">Severity: ${escapeHtml(pin.severity)}</div>`
              : "";
          const popup = `<div style="min-width:180px;max-width:280px;">
            <div style="font-weight:600;margin-bottom:4px;font-size:14px;">${escapeHtml(title)}</div>
            ${severityLine}
            ${description ? `<div style="font-size:13px;margin-top:6px;line-height:1.4;color:#444;">${escapeHtml(description)}</div>` : ""}
          </div>`;
          marker.bindPopup(popup, { closeButton: true });
          pinMarkersRef.current.push(marker);
        });
      }
    });

    return () => {
      overlayLayersRef.current.forEach((layer) => {
        try {
          map.removeLayer(layer);
        } catch (_) {
          /* ignore */
        }
      });
      overlayLayersRef.current = [];
      pinMarkersRef.current.forEach((m) => {
        try {
          map.removeLayer(m);
        } catch (_) {
          /* ignore */
        }
      });
      pinMarkersRef.current = [];
    };
  }, [mapRef, mapReadyVersion, overlays, showPins, previewOverlay?.id]);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    if (previewLayerRef.current) {
      try {
        map.removeLayer(previewLayerRef.current);
      } catch (_) {
        /* ignore */
      }
      previewLayerRef.current = null;
    }

    const preview = normalizePathologyOverlay(previewOverlay);
    if (preview && isPathologyOverlayRenderable(preview)) {
      const layer = addPathologyImageOverlay(map, preview, {
        opacity: previewOpacity,
        className: "pathology-ai-overlay-preview",
        zIndex: 700,
      });
      previewLayerRef.current = layer;
    }

    return () => {
      if (previewLayerRef.current) {
        try {
          map.removeLayer(previewLayerRef.current);
        } catch (_) {
          /* ignore */
        }
        previewLayerRef.current = null;
      }
    };
  }, [mapRef, mapReadyVersion, previewOverlay, previewOpacity]);

  return null;
}
